import React, { useState } from 'react';
import { Contract, FilterState, Task, DEFAULT_TRANSPORTATION_OPTIONS, DEFAULT_STATUS_OPTIONS } from '../types/contract';
import { Company } from '../types/company';
import { BusinessModel, Template } from '../types/template';
import { TemplateService } from '../services/templateService';
import TransportationModal from './TransportationModal';
import StatusEditModal from './StatusEditModal';
import BusinessModelSelectionModal from './BusinessModelSelectionModal';
import TemplateSelectionModal from './TemplateSelectionModal';
import BusinessModelEditor from './BusinessModelEditor';
import TemplateEditor from './TemplateEditor';
import ContractEditor from './ContractEditor';
import { getCurrentAuditTimestamp, formatAuditDate, formatAuditTime, formatNickname } from '../utils/auditFormatters';
import './MainDashboard.css';

interface MainDashboardProps {
  contracts: Contract[];
  onAddContract: () => void;
  onSaveContract: (contract: Contract) => void;
  onDeleteContract: (contractId: string, skipConfirmation?: boolean) => void;
  onDuplicateContract: (contract: Contract) => void;
  onLogout: () => void;
  onShowSettings: () => void;
  onShowCompanyManagement: () => void;
  userInfo: {
    company: string;
    userName: string;
    userId: string;
  };
  onUserInfoChange: (field: string, value: string) => void;
  showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  selectedCompany: Company | null;
  userCompanies: Company[];
  currentUserNickname: string;
  currentUser: any;
  currentUserRole: string;
}

const MainDashboard: React.FC<MainDashboardProps> = ({
  contracts,
  onAddContract,
  onSaveContract,
  onDeleteContract,
  onDuplicateContract,
  onLogout,
  onShowSettings,
  onShowCompanyManagement,
  userInfo,
  onUserInfoChange,
  showToast,
  selectedCompany,
  userCompanies,
  currentUserNickname,
  currentUser,
  currentUserRole
}) => {
  const [filters, setFilters] = useState<FilterState>({
    contract: '',
    product: '',
    transportation: '',
    year: '',
    status: ''
  });
  
  const [openContractId, setOpenContractId] = useState<string | null>(null);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [selectedContractIds, setSelectedContractIds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showTransportationModal, setShowTransportationModal] = useState(false);
  const [showStatusEditModal, setShowStatusEditModal] = useState(false);
  const [editingTaskIndex, setEditingTaskIndex] = useState<number>(-1);
  const [transportationTypes, setTransportationTypes] = useState<string[]>(DEFAULT_TRANSPORTATION_OPTIONS);
  const [draggedTaskIndex, setDraggedTaskIndex] = useState<number | null>(null);
  const [contractHistory, setContractHistory] = useState<Contract[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Template management state
  const [showBusinessModelModal, setShowBusinessModelModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showBusinessModelEditor, setShowBusinessModelEditor] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [selectedBusinessModel, setSelectedBusinessModel] = useState<BusinessModel | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedTransportationMethod, setSelectedTransportationMethod] = useState<string | null>(null);
  const [editingBusinessModel, setEditingBusinessModel] = useState<BusinessModel | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredContracts = contracts.filter(contract => {
    return (
      contract.contractNumber.toLowerCase().includes(filters.contract.toLowerCase()) &&
      contract.product.toLowerCase().includes(filters.product.toLowerCase()) &&
      contract.transportationVehicle.toLowerCase().includes(filters.transportation.toLowerCase()) &&
      contract.year.toString().includes(filters.year) &&
      contract.status.toLowerCase().includes(filters.status.toLowerCase())
    );
  });

  console.log('🔍 MainDashboard - contracts:', contracts.length, 'filteredContracts:', filteredContracts.length);
  console.log('🔍 contracts:', contracts.map(c => ({ id: c.id, number: c.contractNumber })));
  console.log('🔍 filters:', filters);

  // Helper function to get status color (Very Light Green or Very Light Red)
  const getStatusColor = (status: string): string => {
    const greenStatuses = ['presented', 'confirmed', 'completed', 'booked', 'nominated', 'received', 'sent', 'yes'];
    const redStatuses = ['pending', 'in progress', 'cancelled', 'no'];
    
    const statusLower = status.toLowerCase();
    if (greenStatuses.includes(statusLower)) return '#d4f4d3'; // Very Light Green
    if (redStatuses.includes(statusLower)) return '#fdd5d5'; // Very Light Red
    return '#e0e0e0'; // Gray fallback
  };

  // Helper function to check if status is green (for progress calculation)
  const isGreenStatus = (status: string): boolean => {
    const greenStatuses = ['presented', 'confirmed', 'completed', 'booked', 'nominated', 'received', 'sent', 'yes'];
    return greenStatuses.includes(status.toLowerCase());
  };

  const handleOpenContract = (contractId: string) => {
    console.log('🔍 Opening contract:', contractId);
    const contract = contracts.find(c => c.id === contractId);
    if (contract) {
      console.log('✅ Contract found:', contract.contractNumber);
      setOpenContractId(contractId);
      setEditingContract({ ...contract });
      // Initialize history
      setContractHistory([{ ...contract }]);
      setHistoryIndex(0);
      console.log('📝 Contract opened - openContractId:', contractId, 'editingContract:', contract.contractNumber);
    } else {
      console.log('❌ Contract not found:', contractId);
    }
  };

  const handleCloseContract = () => {
    setOpenContractId(null);
    setEditingContract(null);
    setContractHistory([]);
    setHistoryIndex(-1);
  };

  const handleSave = () => {
    if (editingContract) {
      console.log('Saving contract with audit trail:', editingContract);
      console.log('First task audit data:', editingContract.tasks[0]);
      console.log('Tasks with audit trail:', editingContract.tasks.filter(task => task.dataLastEditedBy || task.statusLastEditedBy));
      onSaveContract(editingContract);
      showToast('Contract saved with audit trail!', 'success');
    }
  };
  
  const handleClose = () => {
    handleCloseContract();
  };

  const addToHistory = (contract: Contract) => {
    const newHistory = contractHistory.slice(0, historyIndex + 1);
    newHistory.push({ ...contract });
    setContractHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const previousState = contractHistory[historyIndex - 1];
      setEditingContract({ ...previousState });
      setHistoryIndex(historyIndex - 1);
      showToast('Undo successful', 'info');
    } else {
      showToast('Nothing to undo', 'warning');
    }
  };

  const handleRedo = () => {
    if (historyIndex < contractHistory.length - 1) {
      const nextState = contractHistory[historyIndex + 1];
      setEditingContract({ ...nextState });
      setHistoryIndex(historyIndex + 1);
      showToast('Redo successful', 'info');
    } else {
      showToast('Nothing to redo', 'warning');
    }
  };

  const handleSaveAll = () => {
    if (editingContract) {
      onSaveContract(editingContract);
      showToast('All changes saved!', 'success');
    }
  };

  const handleLoadData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event: any) => {
          try {
            const data = JSON.parse(event.target.result);
            if (editingContract && data.tasks) {
              const updated = { ...editingContract, tasks: data.tasks };
              setEditingContract(updated);
              addToHistory(updated);
              showToast('Data loaded successfully!', 'success');
            }
          } catch (error) {
            showToast('Error loading data. Invalid file format.', 'error');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  // Template management functions
  const handleSelectTemplateClick = () => {
    setShowBusinessModelModal(true);
  };

  const handleBusinessModelSelect = (businessModel: BusinessModel, categoryId?: string, transportationMethod?: string) => {
    setSelectedBusinessModel(businessModel);
    setSelectedCategoryId(categoryId || null);
    setSelectedTransportationMethod(transportationMethod || null);
    setShowBusinessModelModal(false);
    setShowTemplateModal(true);
  };

  const handleTemplateSelect = (template: Template) => {
    console.log('📋 Creating contract from template:', template.name);
    
    // Prompt for contract details (same as + Contract button)
    const contractNumber = prompt('Enter contract number:', `CONTRACT-${String(contracts.length + 1).padStart(3, '0')}`);
    if (!contractNumber) {
      // User cancelled
      return;
    }
    
    const product = prompt('Enter product/commodity name:', '');
    if (product === null) {
      // User cancelled
      return;
    }
    
    const yearInput = prompt('Enter year:', new Date().getFullYear().toString());
    if (!yearInput) {
      // User cancelled
      return;
    }
    
    const year = parseInt(yearInput) || new Date().getFullYear();
    
    // Create a new contract from the template with user-provided details
    const newContract: Contract = {
      id: `temp_${Date.now()}`,
      contractNumber: contractNumber,
      product: product,
      companyId: selectedCompany?.id || '',
      transportationVehicle: template.defaultTransportationVehicle,
      tasks: template.tasks.map((task, index) => ({
        name: task.name,
        status: task.status,
        data: task.data,
        comments: task.comments,
        customStatusOptions: task.customStatusOptions || DEFAULT_STATUS_OPTIONS,
        dataLastEditedBy: currentUserNickname || 'USER',
        dataLastEditedAt: getCurrentAuditTimestamp(),
        statusLastEditedBy: currentUserNickname || 'USER',
        statusLastEditedAt: getCurrentAuditTimestamp()
      })),
      status: 'Pending',
      createdAt: new Date().toISOString(),
      createdBy: currentUserNickname || 'USER',
      createdByName: currentUser?.displayName || currentUser?.email || 'Unknown User',
      createdByUserId: currentUser?.uid || '',
      progress: 0,
      year: year
    };

    console.log('✅ Contract created with', newContract.tasks.length, 'tasks');
    
    // Increment template usage
    TemplateService.incrementTemplateUsage(template.id).catch(console.error);
    
    setEditingContract(newContract);
    setShowTemplateModal(false);
    setSelectedBusinessModel(null);

    showToast(`Contract created from template: ${template.name}`, 'success');
  };

  const handleNewTrade = () => {
    if (editingContract) {
      onDuplicateContract(editingContract);
      showToast('New trade created from current contract!', 'success');
    }
  };

  const handleExportCSV = async () => {
    if (!editingContract) return;
    
    try {
      const { ExcelExportService } = await import('../services/excelExportService');
      ExcelExportService.exportContractDetails(editingContract);
      showToast('Contract exported to Excel!', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showToast('Export failed. Please try again.', 'error');
    }
  };

  const handleGlobalDropdown = () => {
    if (!editingContract) return;
    
    const status = prompt('Enter status to apply to all tasks:', 'Pending');
    if (status) {
      const updatedTasks = editingContract.tasks.map(task => ({
        ...task,
        status: status
      }));
      const updated = { ...editingContract, tasks: updatedTasks };
      setEditingContract(updated);
      addToHistory(updated);
      showToast(`Applied "${status}" to all tasks!`, 'success');
    }
  };

  const handlePrint = () => {
    if (!editingContract) return;
    
    const progress = calculateProgress(editingContract);
    const printContent = `
      <html>
        <head>
          <title>Contract ${editingContract.contractNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #2c3e50; border-bottom: 3px solid #3498db; padding-bottom: 10px; }
            .contract-info { margin: 20px 0; padding: 15px; background: #f8f9fa; border-radius: 8px; }
            .contract-info p { margin: 8px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #34495e; color: white; padding: 12px; text-align: left; }
            td { padding: 10px; border-bottom: 1px solid #e1e8ed; }
            tr:hover { background: #f8f9fa; }
            .filled { background: #d4edda; }
            .empty { background: #ffe6e6; }
          </style>
        </head>
        <body>
          <h1>Contract: ${editingContract.contractNumber}</h1>
          <div class="contract-info">
            <p><strong>Product:</strong> ${editingContract.product}</p>
            <p><strong>Transportation:</strong> ${editingContract.transportationVehicle}</p>
            <p><strong>Year:</strong> ${editingContract.year}</p>
            <p><strong>Status:</strong> ${editingContract.status}</p>
            <p><strong>Progress:</strong> ${progress}%</p>
            <p><strong>Created:</strong> ${new Date(editingContract.createdAt).toLocaleString()}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Task</th>
                <th>Status</th>
                <th>Data</th>
                <th>Comments</th>
              </tr>
            </thead>
            <tbody>
              ${editingContract.tasks.map(task => `
                <tr>
                  <td>${task.name}</td>
                  <td>${task.status}</td>
                  <td class="${task.data ? 'filled' : 'empty'}">${task.data || '-'}</td>
                  <td>${task.comments || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
    }
    
    showToast('Print preview opened!', 'info');
  };

  const calculateProgress = (contract: Contract) => {
    // Count only tasks with GREEN statuses (completed/positive tasks)
    const greenTasks = contract.tasks.filter(task => isGreenStatus(task.status)).length;
    return Math.round((greenTasks / contract.tasks.length) * 100);
  };

  const handleTaskChange = (taskIndex: number, field: 'name' | 'status' | 'data' | 'comments', value: string) => {
    if (editingContract) {
      const updatedTasks = [...editingContract.tasks];
      const oldValue = updatedTasks[taskIndex][field];
      
      // Update the field value
      updatedTasks[taskIndex] = { ...updatedTasks[taskIndex], [field]: value };
      
      // Track audit trail for DATA column changes
      if (field === 'data' && oldValue !== value) {
        console.log('🔍 Current user nickname from prop:', currentUserNickname);
        const nickname = currentUserNickname || 'USER';
        updatedTasks[taskIndex].dataLastEditedBy = nickname;
        updatedTasks[taskIndex].dataLastEditedAt = getCurrentAuditTimestamp();
        console.log('DATA audit trail set:', {
          taskIndex,
          field,
          oldValue,
          value,
          currentUserNickname: currentUserNickname,
          nickname: nickname,
          timestamp: getCurrentAuditTimestamp()
        });
      }
      
      // Track audit trail for STATUS column changes
      if (field === 'status' && oldValue !== value) {
        const nickname = currentUserNickname || 'USER';
        updatedTasks[taskIndex].statusLastEditedBy = nickname;
        updatedTasks[taskIndex].statusLastEditedAt = getCurrentAuditTimestamp();
        console.log('STATUS audit trail set:', {
          taskIndex,
          field,
          oldValue,
          value,
          nickname: nickname,
          timestamp: getCurrentAuditTimestamp()
        });
      }
      
      const updated = { ...editingContract, tasks: updatedTasks };
      setEditingContract(updated);
      
      // Add to history only for significant changes (data and comments)
      if (field === 'data' || field === 'comments') {
        addToHistory(updated);
      }
    }
  };

  const handleAddTask = (afterIndex: number) => {
    if (editingContract) {
      const newTask: Task = {
        name: 'New Task',
        status: 'Pending',
        data: '',
        comments: ''
      };
      const updatedTasks = [...editingContract.tasks];
      updatedTasks.splice(afterIndex + 1, 0, newTask);
      const updated = { ...editingContract, tasks: updatedTasks };
      setEditingContract(updated);
      addToHistory(updated);
    }
  };

  const handleDeleteTask = (taskIndex: number) => {
    if (editingContract && window.confirm('Are you sure you want to delete this task?')) {
      const updatedTasks = editingContract.tasks.filter((_, index) => index !== taskIndex);
      const updated = { ...editingContract, tasks: updatedTasks };
      setEditingContract(updated);
      addToHistory(updated);
    }
  };

  const handleSelectContract = (contractId: string) => {
    const newSelected = new Set(selectedContractIds);
    if (newSelected.has(contractId)) {
      newSelected.delete(contractId);
    } else {
      newSelected.add(contractId);
    }
    setSelectedContractIds(newSelected);
    setSelectAll(newSelected.size === filteredContracts.length && filteredContracts.length > 0);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedContractIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedContractIds(new Set(filteredContracts.map(c => c.id)));
      setSelectAll(true);
    }
  };

  const handleBulkDelete = () => {
    if (selectedContractIds.size === 0) {
      showToast('Please select contracts to delete', 'warning');
      return;
    }
    
    const count = selectedContractIds.size;
    const confirmed = window.confirm(
      `⚠️ WARNING: You are about to delete ${count} contract(s)\n\n` +
      `This action cannot be undone!\n\n` +
      `Click OK to delete ALL selected contracts, or Cancel to abort.`
    );
    
    if (!confirmed) {
      return;
    }
    
    // Delete all selected contracts with skipConfirmation flag
    selectedContractIds.forEach(id => {
      onDeleteContract(id, true); // Skip individual confirmation
    });
    
    setSelectedContractIds(new Set());
    setSelectAll(false);
    showToast(`${count} contract(s) deleted successfully!`, 'success');
  };

  const handleBulkExport = async () => {
    if (selectedContractIds.size === 0) {
      showToast('Please select contracts to export', 'warning');
      return;
    }
    
    try {
      const selectedContracts = contracts.filter(c => selectedContractIds.has(c.id));
      const { ExcelExportService } = await import('../services/excelExportService');
      
      ExcelExportService.exportContracts(selectedContracts, {
        includeTasks: true,
        includeComments: true,
        includeStatus: true,
        includeDates: true,
        filename: `selected_contracts_export_${new Date().toISOString().split('T')[0]}.xls`
      });
      
      showToast(`Exported ${selectedContracts.length} contract(s) to Excel!`, 'success');
    } catch (error) {
      console.error('Export error:', error);
      showToast('Export failed. Please try again.', 'error');
    }
  };

  const handleExportAll = async () => {
    if (contracts.length === 0) {
      showToast('No contracts to export', 'warning');
      return;
    }
    
    try {
      const { ExcelExportService } = await import('../services/excelExportService');
      
      ExcelExportService.exportContracts(contracts, {
        includeTasks: true,
        includeComments: true,
        includeStatus: true,
        includeDates: true,
        filename: `all_contracts_export_${new Date().toISOString().split('T')[0]}.xls`
      });
      
      showToast(`Exported all ${contracts.length} contract(s) to Excel!`, 'success');
    } catch (error) {
      console.error('Export error:', error);
      showToast('Export failed. Please try again.', 'error');
    }
  };

  const handleBulkDuplicate = () => {
    if (selectedContractIds.size === 0) {
      showToast('Please select contracts to duplicate', 'warning');
      return;
    }
    
    const selectedContracts = contracts.filter(c => selectedContractIds.has(c.id));
    selectedContracts.forEach(contract => {
      onDuplicateContract(contract);
    });
    
    setSelectedContractIds(new Set());
    setSelectAll(false);
    showToast(`Duplicated ${selectedContracts.length} contract(s) successfully!`, 'success');
  };

  const handleSaveTransportationTypes = (types: string[]) => {
    setTransportationTypes(types);
    showToast('Transportation types updated successfully!', 'success');
  };

  const handleSaveStatusOptions = (statuses: string[]) => {
    if (editingContract && editingTaskIndex >= 0) {
      const updatedTasks = [...editingContract.tasks];
      updatedTasks[editingTaskIndex] = {
        ...updatedTasks[editingTaskIndex],
        customStatusOptions: statuses
      };
      setEditingContract({ ...editingContract, tasks: updatedTasks });
      showToast('Status options updated successfully!', 'success');
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedTaskIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedTaskIndex === null || !editingContract) return;
    
    const updatedTasks = [...editingContract.tasks];
    const [draggedTask] = updatedTasks.splice(draggedTaskIndex, 1);
    updatedTasks.splice(dropIndex, 0, draggedTask);
    
    setEditingContract({ ...editingContract, tasks: updatedTasks });
    setDraggedTaskIndex(null);
  };

  const handleRowClick = (contractId: string, isCurrentlyOpen: boolean) => {
    if (!isCurrentlyOpen) {
      handleOpenContract(contractId);
    }
  };

  return (
    <div className="main-dashboard">
      {/* Header */}
      <div className="app-main-header">
        <div className="header-left">
          <div className="header-logo-section">
            <img src="/logo.svg" alt="TEP Logo" className="header-logo" />
            <p className="header-subtitle">Dashboard for Monitoring of Trade Execution</p>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={onShowCompanyManagement} className="company-btn" title="Manage Companies">🏢 Companies</button>
          <button onClick={onShowSettings} className="settings-btn" title="Settings">⚙️ Settings</button>
          <button onClick={onLogout} className="logout-btn">Logout</button>
        </div>
      </div>

      {/* User Info Section */}
      <div className="user-info-bar">
        <div className="user-info-field">
          <label>Company:</label>
          <input
            type="text"
            value={userInfo.company}
            onChange={(e) => onUserInfoChange('company', e.target.value)}
          />
        </div>
        <div className="user-info-field">
          <label>User Name:</label>
          <input
            type="text"
            value={userInfo.userName}
            onChange={(e) => onUserInfoChange('userName', e.target.value)}
          />
        </div>
        <div className="user-info-field">
          <label>User Nickname:</label>
          <div className="nickname-display">
            {currentUserNickname ? (
              <span className="user-nickname-badge">{currentUserNickname}</span>
            ) : (
              <span className="no-nickname-text">⚠️ No nickname assigned</span>
            )}
          </div>
        </div>
      </div>

      {/* No Company Message - Only show for non-Master users */}
      {!selectedCompany && currentUserRole !== 'MASTER' && (
        <div className="no-company-message">
          <div className="no-company-content">
            <h3>🏢 Welcome to Trade Execution Platform!</h3>
            <p>You don't have access to any companies yet.</p>
            <p>Please contact your administrator to get an invitation code.</p>
            <div style={{marginTop: '20px', padding: '15px', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107'}}>
              <strong>ℹ️ Note:</strong> Only the platform owner can create companies. 
              You need an invitation code to join an existing company.
            </div>
          </div>
        </div>
      )}

      {/* Master User Message - Show when Master has no companies */}
      {!selectedCompany && currentUserRole === 'MASTER' && userCompanies.length === 0 && (
        <div className="no-company-message">
          <div className="no-company-content">
            <h3>🏢 Welcome to Trade Execution Platform!</h3>
            <p>No companies have been created yet.</p>
            <p>As a Master user, you can create the first company.</p>
            <div style={{marginTop: '20px', padding: '15px', background: '#d1ecf1', borderRadius: '8px', border: '1px solid #bee5eb'}}>
              <strong>ℹ️ Note:</strong> Click the "Companies" button to create your first company.
            </div>
          </div>
        </div>
      )}

      {/* Contract Management Section */}
      {selectedCompany && (
        <>
          {/* Filtering Interface */}
      <div className="filtering-interface">
        <input
          type="text"
          placeholder="Contract"
          value={filters.contract}
          onChange={(e) => handleFilterChange('contract', e.target.value)}
          className="filter-input"
        />
        <input
          type="text"
          placeholder="Product"
          value={filters.product}
          onChange={(e) => handleFilterChange('product', e.target.value)}
          className="filter-input"
        />
        <select
          value={filters.transportation}
          onChange={(e) => handleFilterChange('transportation', e.target.value)}
          className="filter-select"
        >
          <option value="">Transportation vehicle</option>
          <option value="Vessel">Vessel</option>
          <option value="Container">Container</option>
          <option value="Truck">Truck</option>
          <option value="Air">Air</option>
          <option value="Multimodal">Multimodal</option>
          <option value="Custom">Custom</option>
        </select>
        <input
          type="text"
          placeholder="Year"
          value={filters.year}
          onChange={(e) => handleFilterChange('year', e.target.value)}
          className="filter-input"
        />
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="filter-select"
        >
          <option value="">Status</option>
          <option value="Pending">Pending</option>
          <option value="Completed">Completed</option>
        </select>
      </div>

      {/* Bulk Actions Bar */}
      {selectedContractIds.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-info">
            <strong>{selectedContractIds.size}</strong> contract(s) selected
          </div>
          <div className="bulk-buttons">
            <button onClick={handleBulkDuplicate} className="bulk-btn duplicate-btn">
              📋 Duplicate
            </button>
            <button onClick={handleBulkExport} className="bulk-btn export-btn">
              📊 Export Selected to Excel
            </button>
            <button onClick={handleExportAll} className="bulk-btn export-btn">
              📈 Export All to Excel
            </button>
            <button onClick={handleBulkDelete} className="bulk-btn delete-btn">
              🗑️ Delete Selected
            </button>
            <button onClick={() => { setSelectedContractIds(new Set()); setSelectAll(false); }} className="bulk-btn cancel-btn">
              ✕ Cancel
            </button>
          </div>
        </div>
      )}

      {/* Contract List Header */}
      <div className="contract-list-header">
        <div className="table-header-row">
          <div className="header-col col-checkbox">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
              title="Select All"
            />
          </div>
          <div className="header-col col-contract">Contract</div>
          <div className="header-col col-product">Product</div>
          <div className="header-col col-transportation">Transportation vehicle</div>
          <div className="header-col col-year">Year</div>
          <div className="header-col col-status">Status</div>
          <div className="header-col col-progress">Progress</div>
          <div className="header-col col-actions">Actions</div>
        </div>
        <div className="add-contract-area">
          <button 
            className="select-template-btn"
            onClick={handleSelectTemplateClick}
            title="Select Template"
          >
            📋 Select Template
          </button>
          <button onClick={onAddContract} className="add-contract-btn">+ Default Template</button>
        </div>
      </div>

      {/* Contract List (Folded Lines) */}
      <div className="contract-list">
        {filteredContracts.length === 0 ? (
          <div className="no-contracts">No contracts found. Click "+ Default Template" to create one.</div>
        ) : (
          filteredContracts.map((contract) => {
            console.log('🔍 Rendering contract:', contract.contractNumber, 'with ID:', contract.id);
            const progress = calculateProgress(contract);
            const isOpen = openContractId === contract.id;
            
            return (
              <div key={contract.id} className="contract-row-container">
                <div 
                  className={`contract-row ${isOpen ? 'open' : ''} ${selectedContractIds.has(contract.id) ? 'selected' : ''}`}
                  onClick={() => handleRowClick(contract.id, isOpen)}
                >
                  <div className="row-col col-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedContractIds.has(contract.id)}
                      onChange={() => handleSelectContract(contract.id)}
                    />
                  </div>
                  <div className="row-col col-contract" style={{ fontWeight: 'bold' }}>{contract.contractNumber}</div>
                  <div className="row-col col-product" style={{ fontWeight: 'bold' }}>{contract.product}</div>
                  <div className="row-col col-transportation" style={{ fontWeight: 'bold' }}>{contract.transportationVehicle}</div>
                  <div className="row-col col-year" style={{ fontWeight: 'bold' }}>{contract.year}</div>
                  <div className="row-col col-status">
                    <span className={`status-badge status-${contract.status.toLowerCase().replace(' ', '-')}`}>
                      {contract.status}
                    </span>
                  </div>
                  <div className="row-col col-progress">
                    <div className="progress-bar-mini">
                      <div 
                        className="progress-fill-mini"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">{progress}%</span>
                  </div>
                  <div className="row-col col-actions" onClick={(e) => e.stopPropagation()}>
                    {isOpen ? (
                      <button onClick={handleCloseContract} className="action-btn close-btn">Close</button>
                    ) : (
                      <button onClick={() => handleOpenContract(contract.id)} className="action-btn open-btn">Open</button>
                    )}
                  </div>
                </div>

                {/* Opened Contract Work Area */}
                {isOpen && editingContract && (
                  <div className="contract-work-area">
                    {/* Top Header with Contract Number and Save/Close */}
                    <div className="work-area-header">
                      <div className="contract-number-section">
                        <label>Contract Number:</label>
                        <input
                          type="text"
                          value={editingContract!.contractNumber}
                          onChange={(e) => setEditingContract({ ...editingContract!, contractNumber: e.target.value })}
                          className="contract-number-input"
                        />
                      </div>
                      <div className="header-buttons">
                        <button onClick={handleSave} className="save-btn">Save</button>
                        <button onClick={handleClose} className="close-contract-btn">Close</button>
                      </div>
                    </div>

                    {/* Task Buttons Section */}
                    <div className="task-buttons-section">
                      <button className="task-btn undo-btn" onClick={handleUndo} title="Undo (Ctrl+Z)">↶ Undo</button>
                      <button className="task-btn redo-btn" onClick={handleRedo} title="Redo (Ctrl+Y)">↷ Redo</button>
                      <div className="button-divider"></div>
                      <button className="task-btn" onClick={handleSaveAll}>💾 Save All</button>
                      <button className="task-btn" onClick={handleLoadData}>📥 Load Data</button>
                      <button className="task-btn" onClick={handleNewTrade}>📋 New Trade</button>
                      <button className="task-btn" onClick={handleExportCSV}>📊 Export to Excel</button>
                      <button className="task-btn" onClick={handleGlobalDropdown}>🔽 Global Dropdown</button>
                      <button className="task-btn" onClick={handlePrint}>🖨️ Print</button>
                      <button className="task-btn" onClick={() => {
                        if (editingContract) {
                          const nickname = currentUserNickname || 'TEST';
                          const updatedTasks = [...editingContract.tasks];
                          updatedTasks[0].dataLastEditedBy = nickname;
                          updatedTasks[0].dataLastEditedAt = getCurrentAuditTimestamp();
                          updatedTasks[0].statusLastEditedBy = nickname;
                          updatedTasks[0].statusLastEditedAt = getCurrentAuditTimestamp();
                          setEditingContract({ ...editingContract, tasks: updatedTasks });
                          console.log('Test audit trail set for task 0:', updatedTasks[0]);
                          console.log('Using nickname:', nickname);
                          showToast('Test audit trail set!', 'info');
                        } else {
                          console.log('Missing editingContract');
                          showToast('Missing contract!', 'error');
                        }
                      }}>🧪 Test Audit</button>
                    </div>

                    {/* Contract Details Section - All in One Line */}
                    <div className="contract-details-section">
                      {/* Contract Creation Audit Info */}
                      {/* Contract Details Row - ALL ELEMENTS IN ONE HORIZONTAL LINE */}
                      <div className="contract-details-row">
                        {/* Created by section */}
                        {editingContract!.createdBy && (
                          <div className="detail-field created-by-field">
                            <span className="audit-label">Created by:</span>
                            <span className="audit-nickname" title={editingContract!.createdByName || 'Unknown'}>
                              {editingContract!.createdBy.length > 10 ? 
                                (editingContract!.createdBy.substring(0, 3).toUpperCase() + '...') : 
                                editingContract!.createdBy
                              }
                            </span>
                            {editingContract!.createdByName && (
                              <span className="audit-name">({editingContract!.createdByName})</span>
                            )}
                            <span className="audit-on">on</span>
                            <span className="audit-date">{formatAuditDate(editingContract!.createdAt)}</span>
                            <span className="audit-at">at</span>
                            <span className="audit-time">{formatAuditTime(editingContract!.createdAt)}</span>
                          </div>
                        )}
                        
                        <div className="detail-field">
                          <label>Contract Number:</label>
                          <input
                            type="text"
                            value={editingContract!.contractNumber}
                            onChange={(e) => setEditingContract({ ...editingContract!, contractNumber: e.target.value })}
                          />
                        </div>
                        
                        <div className="detail-field">
                          <label>Transportation By:</label>
                          <select
                            value={editingContract!.transportationVehicle}
                            onChange={(e) => setEditingContract({ ...editingContract!, transportationVehicle: e.target.value })}
                          >
                            {transportationTypes.map((type, idx) => (
                              <option key={idx} value={type}>{type}</option>
                            ))}
                          </select>
                          <button 
                            className="manage-btn"
                            onClick={() => setShowTransportationModal(true)}
                          >
                            Add/Remove
                          </button>
                        </div>
                        
                        <div className="detail-field status-completed-field">
                          <div className="status-bar">
                            <div className="status-bar-fill" style={{ width: `${progress}%` }}></div>
                          </div>
                          <span className="completion-text">{progress}% Completed:</span>
                          <select 
                            className="completed-select"
                            value={editingContract!.status === 'Completed' ? 'Yes' : 'No'}
                            onChange={(e) => {
                              if (e.target.value === 'Yes') {
                                setEditingContract({ ...editingContract!, status: 'Completed' });
                              }
                            }}
                          >
                            <option value="No">No</option>
                            <option value="Yes">Yes</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Tasks Table Header */}
                    <div className="tasks-table-header">
                      <div className="task-header-col col-line-number">#</div>
                      <div className="task-header-col col-task-name">Task</div>
                      <div className="task-header-col col-task-status">Status</div>
                      <div className="task-header-col col-task-data">Data</div>
                      <div className="task-header-col col-task-comments">Comments</div>
                      <div className="task-header-col col-task-actions">Actions</div>
                      <div className="task-header-col col-spacer"></div>
                      <div className="task-header-col col-audit-dropdown">Audit</div>
                    </div>

                    {/* Tasks Table */}
                    <div className="tasks-table">
                      {editingContract!.tasks.map((task, index) => {
                        const isFilled = task.data && task.data.trim() !== '';
                        
                        
                        // Handle existing contracts that may have empty customStatusOptions
                        const taskStatusOptions = (task.customStatusOptions && task.customStatusOptions.length > 0) 
                          ? task.customStatusOptions 
                          : DEFAULT_STATUS_OPTIONS;

                        return (
                          <div 
                            key={index} 
                            className="task-row" 
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                          >
                            <div className="task-col col-line-number">
                              <span className="line-number">{index + 1}</span>
                            </div>
                            <div className="task-col col-task-name" style={{ backgroundColor: getStatusColor(task.status), padding: '2px' }}>
                              <input
                                type="text"
                                value={task.name}
                                onChange={(e) => handleTaskChange(index, 'name', e.target.value)}
                                placeholder="Task name..."
                                style={{ 
                                  backgroundColor: 'transparent', 
                                  color: 'black', 
                                  fontWeight: 'bold',
                                  border: '1px solid rgba(0, 0, 0, 0.2)'
                                }}
                              />
                            </div>
                            <div className="task-col col-task-status" style={{ padding: '2px' }}>
                              <div className="status-wrapper">
                                <select
                                  value={task.status}
                                  onChange={(e) => handleTaskChange(index, 'status', e.target.value)}
                                  style={{
                                    backgroundColor: getStatusColor(task.status),
                                    color: 'black',
                                    fontWeight: 'bold',
                                    border: '1px solid rgba(0, 0, 0, 0.2)',
                                    borderRadius: '3px',
                                    padding: '2px 4px'
                                  }}
                                >
                                  {taskStatusOptions.map((statusOption, idx) => (
                                    <option key={idx} value={statusOption}>{statusOption}</option>
                                  ))}
                                </select>
                                <button 
                                  className="edit-status-btn" 
                                  onClick={() => {
                                    setEditingTaskIndex(index);
                                    setShowStatusEditModal(true);
                                  }}
                                  title="Edit Status Options"
                                >
                                  Edit
                                </button>
                              </div>
                            </div>
                            <div className={`task-col col-task-data ${isFilled ? 'filled' : 'empty'}`} style={{ backgroundColor: getStatusColor(task.status), padding: '2px' }}>
                              <input
                                type="text"
                                value={task.data}
                                onChange={(e) => handleTaskChange(index, 'data', e.target.value)}
                                placeholder="Enter data..."
                                style={{ 
                                  backgroundColor: 'transparent', 
                                  color: 'black', 
                                  fontWeight: 'bold',
                                  border: '1px solid rgba(0, 0, 0, 0.2)'
                                }}
                              />
                            </div>
                            <div className="task-col col-task-comments">
                              <input
                                type="text"
                                value={task.comments}
                                onChange={(e) => handleTaskChange(index, 'comments', e.target.value)}
                                placeholder="Enter comments..."
                              />
                            </div>
                            <div className="task-col col-task-actions">
                              <button onClick={() => handleAddTask(index)} className="task-action-btn add-task-btn" title="Add task below">+</button>
                              <button onClick={() => handleDeleteTask(index)} className="task-action-btn delete-task-btn" title="Delete task">-</button>
                              <button className="task-action-btn move-btn" title="Drag to reorder">☰</button>
                            </div>
                            
                            {/* Spacer Column */}
                            <div className="task-col col-spacer"></div>
                            
                            {/* Audit Trail Dropdown */}
                            <div className="task-col col-audit-dropdown">
                              <select className="audit-dropdown" title="Audit Trail Information">
                                {(() => {
                                  // Determine the latest editor
                                  let latestEditor = null;
                                  let latestTimestamp = null;
                                  
                                  if (task.statusLastEditedBy && task.statusLastEditedAt) {
                                    latestEditor = task.statusLastEditedBy;
                                    latestTimestamp = task.statusLastEditedAt;
                                  }
                                  
                                  if (task.dataLastEditedBy && task.dataLastEditedAt) {
                                    const dataTimestamp = task.dataLastEditedAt;
                                    if (!latestTimestamp || new Date(dataTimestamp) > new Date(latestTimestamp)) {
                                      latestEditor = task.dataLastEditedBy;
                                      latestTimestamp = dataTimestamp;
                                    }
                                  }
                                  
                                  // Show latest editor as default, or "-" if no edits
                                  const defaultText = latestEditor ? formatNickname(latestEditor) : "-";
                                  
                                  return (
                                    <>
                                      <option value="">{defaultText}</option>
                                      {task.statusLastEditedBy && (
                                        <option value="status">
                                          Status: {formatNickname(task.statusLastEditedBy)}{'\n'}{formatAuditDate(task.statusLastEditedAt)}{'\n'}{formatAuditTime(task.statusLastEditedAt)}
                                        </option>
                                      )}
                                      {task.dataLastEditedBy && (
                                        <option value="data">
                                          Data: {formatNickname(task.dataLastEditedBy)}{'\n'}{formatAuditDate(task.dataLastEditedAt)}{'\n'}{formatAuditTime(task.dataLastEditedAt)}
                                        </option>
                                      )}
                                    </>
                                  );
                                })()}
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Transportation Types Modal */}
      <TransportationModal
        isOpen={showTransportationModal}
        onClose={() => setShowTransportationModal(false)}
        onSave={handleSaveTransportationTypes}
        currentTypes={transportationTypes}
      />

      {/* Status Edit Modal */}
      <StatusEditModal
        isOpen={showStatusEditModal}
        onClose={() => {
          setShowStatusEditModal(false);
          setEditingTaskIndex(-1);
        }}
        onSave={handleSaveStatusOptions}
        currentStatuses={(() => {
          if (!editingContract || editingTaskIndex < 0 || !editingContract.tasks[editingTaskIndex]) {
            return DEFAULT_STATUS_OPTIONS;
          }
          const task = editingContract.tasks[editingTaskIndex];
          return (task.customStatusOptions && task.customStatusOptions.length > 0) 
            ? task.customStatusOptions 
            : DEFAULT_STATUS_OPTIONS;
        })()}
        taskIndex={editingTaskIndex}
      />

      {/* Business Model Selection Modal */}
      <BusinessModelSelectionModal
        isOpen={showBusinessModelModal}
        onClose={() => setShowBusinessModelModal(false)}
        onSelectBusinessModel={handleBusinessModelSelect}
        currentUserRole={currentUserRole}
        onEditBusinessModel={(businessModel) => {
          setEditingBusinessModel(businessModel);
          setShowBusinessModelModal(false);
          setShowBusinessModelEditor(true);
        }}
      />

      {/* Business Model Editor */}
      <BusinessModelEditor
        isOpen={showBusinessModelEditor}
        onClose={() => {
          setShowBusinessModelEditor(false);
          setEditingBusinessModel(null);
        }}
        businessModel={editingBusinessModel}
        onSave={() => {
          showToast('Business model updated successfully!', 'success');
          setShowBusinessModelEditor(false);
          setEditingBusinessModel(null);
        }}
      />

      <TemplateSelectionModal
        isOpen={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false);
          setSelectedBusinessModel(null);
          setSelectedCategoryId(null);
          setSelectedTransportationMethod(null);
        }}
        onBack={() => {
          setShowTemplateModal(false);
          setShowBusinessModelModal(true);
        }}
        businessModel={selectedBusinessModel}
        categoryId={selectedCategoryId}
        transportationMethod={selectedTransportationMethod}
        onSelectTemplate={handleTemplateSelect}
        currentUserRole={currentUserRole}
        onEditTemplate={(template) => {
          setEditingTemplate(template);
          setShowTemplateModal(false);
          setShowTemplateEditor(true);
        }}
        onCreateTemplate={() => {
          setEditingTemplate(null);
          setShowTemplateModal(false);
          setShowTemplateEditor(true);
        }}
      />

      {selectedBusinessModel && (
        <TemplateEditor
          isOpen={showTemplateEditor}
          onClose={() => {
            setShowTemplateEditor(false);
            setEditingTemplate(null);
          }}
          businessModel={selectedBusinessModel}
          template={editingTemplate}
          onSave={() => {
            showToast(editingTemplate ? 'Template updated successfully!' : 'Template created successfully!', 'success');
            setShowTemplateEditor(false);
            setEditingTemplate(null);
            setShowTemplateModal(true);
          }}
        />
      )}

      {/* Contract Editor - Show only for template-created contracts */}
      {editingContract && editingContract.id.startsWith('temp_') && (
        <ContractEditor
          contract={editingContract}
          onSave={(contract) => {
            console.log('💾 Saving contract from template:', contract.contractNumber || 'New Contract');
            onSaveContract(contract);
            setEditingContract(null);
            showToast('Contract saved successfully!', 'success');
          }}
          onDelete={(contractId) => {
            onDeleteContract(contractId);
            setEditingContract(null);
            showToast('Contract deleted successfully!', 'success');
          }}
          onBack={() => {
            setEditingContract(null);
          }}
        />
      )}
        </>
      )}
    </div>
  );
};

export default MainDashboard;
